import React, { useMemo } from "react";
import { TableRow, useTheme } from "@mui/material";
import { Tablecell } from "../../component/Methods";
import { useSelector } from "react-redux";
import { selectStrategies, selectFunds } from "../../Redux/Reducers/selectors";
import { POSITION_ROW_HEIGHT } from "./styles/positionTableConstants";
import {
  MarketTooltip,
  TradesTooltip,
  getTradesTooltipValues,
} from "./styles/positionTableTooltips";
import {
  getRowDisplayValues,
  getSideColor,
} from "./utils/portfolioExport";

const PositionTable = React.memo((props) => {
  const { row, fundVal, strategyVal, visible, positionType = "long" } = props;
  const isLong = positionType === "long";
  const theme = useTheme();
  const funds = useSelector(selectFunds);
  const strategies = useSelector(selectStrategies);

  const display = useMemo(
    () => getRowDisplayValues(row, isLong),
    [row, isLong]
  );
  const trades = useMemo(
    () => getTradesTooltipValues(row, isLong),
    [row, isLong]
  );

  const cellBorder = `1px solid ${theme.palette.grey[300]}`;

  const handlePosition = () => {
    if (
      !fundVal ||
      !strategyVal ||
      fundVal.toUpperCase() === "ALL" ||
      strategyVal.toUpperCase() === "ALL"
    ) {
      return;
    }
    funds?.find((i) => i.abbreviation == fundVal);
    strategies?.find((i) => i.name == strategyVal);
  };

  return (
    <TableRow
      sx={{
        height: POSITION_ROW_HEIGHT,
        maxHeight: POSITION_ROW_HEIGHT,
        backgroundColor: theme.palette.grey[100],
        "&:hover": {
          background: theme.palette.grey[200],
          cursor: "pointer",
        },
        "& .MuiTableCell-root": { py: 0, lineHeight: 1.2 },
      }}
      onClick={handlePosition}
    >
      <TradesTooltip
        enabled={trades.size.changed}
        title="SIZE"
        yest={trades.size.yest}
        today={trades.size.today}
      >
        <Tablecell
          style={{ width: "50px" }}
          align="right"
          sx={{
            paddingRight: isLong ? "5px" : "2px",
            color: theme.palette.text.primary,
            border: cellBorder,
          }}
        >
          {display.size}
        </Tablecell>
      </TradesTooltip>

      <TradesTooltip
        enabled={trades.shares.changed}
        title="SHARES"
        yest={trades.shares.yest}
        today={trades.shares.today}
      >
        <Tablecell
          align="right"
          sx={{
            paddingRight: "5px",
            color: theme.palette.text.primary,
            border: cellBorder,
          }}
        >
          {display.shares}
        </Tablecell>
      </TradesTooltip>

      <Tablecell
        style={{
          width: "20px",
          paddingLeft: "5px",
          cursor: "pointer",
          color: theme.palette.text.primary,
          border: cellBorder,
        }}
        align="left"
      >
        {display.ticker}
      </Tablecell>

      <MarketTooltip dataSource={row?.dataSource} fetched={row?.fetched}>
        <Tablecell
          style={{
            width: "20px",
            paddingRight: "5px",
            backgroundColor: row?.valid ? "#baffc8" : "#ffdcdc",
            border: cellBorder,
          }}
          align="right"
        >
          {display.last}
        </Tablecell>
      </MarketTooltip>

      <MarketTooltip dataSource={row?.dataSource} fetched={row?.fetched}>
        <Tablecell
          style={{
            width: "20px",
            paddingRight: "5px",
            backgroundColor: row?.valid ? "#baffc8" : "#ffdcdc",
            border: cellBorder,
          }}
          align="right"
        >
          {display.current}
        </Tablecell>
      </MarketTooltip>

      {display.showChg ? (
        <Tablecell
          align="right"
          style={{
            color: getSideColor(theme, display.chRaw, isLong),
            paddingRight: "2px",
            border: cellBorder,
          }}
        >
          {display.ch}
          {isLong && " "}
        </Tablecell>
      ) : (
        <Tablecell align="right" style={{ border: cellBorder }} />
      )}

      {display.showPl ? (
        <TradesTooltip
          enabled={trades.pl.changed}
          title="P(L)"
          yest={trades.pl.yest}
          today={trades.pl.today}
        >
          <Tablecell
            align="right"
            style={{
              color: getSideColor(theme, display.plRaw, true),
              paddingRight: "2px",
              border: cellBorder,
            }}
          >
            {display.pl}
            {isLong && " "}
          </Tablecell>
        </TradesTooltip>
      ) : (
        <Tablecell align="right" style={{ border: cellBorder }} />
      )}

      {display.showChPct ? (
        <Tablecell
          style={{
            color: getSideColor(theme, display.chPctRaw, isLong),
            width: "20px",
            paddingRight: "2px",
            border: cellBorder,
          }}
          align="right"
        >
          {display.chPct}
        </Tablecell>
      ) : (
        <Tablecell align="right" style={{ border: cellBorder }} />
      )}

      {visible ? (
        <>
          <Tablecell
            align="right"
            style={{
              paddingRight: "5px",
              color: theme.palette.text.primary,
              border: cellBorder,
            }}
          >
            {display.plAvgCost}
          </Tablecell>
          <Tablecell
            align="right"
            style={{
              paddingRight: "5px",
              color: theme.palette.text.primary,
              border: cellBorder,
            }}
          >
            {display.pctAvgCost}
          </Tablecell>
        </>
      ) : null}

      <TradesTooltip
        enabled={trades.cost.changed}
        title="AVG COST"
        yest={trades.cost.yest}
        today={trades.cost.today}
      >
        <Tablecell
          align="right"
          style={{
            paddingRight: "5px",
            color: theme.palette.text.primary,
            border: cellBorder,
          }}
        >
          {isLong ? "" : " "}
          {display.cost}
        </Tablecell>
      </TradesTooltip>

      <Tablecell
        align="right"
        style={{
          color: getSideColor(theme, display.tdgRaw, true, {
            treatZeroAsNeutral: true,
          }),
          border: cellBorder,
          width: "20px",
          paddingRight: "5px",
        }}
      >
        {display.tdg}
      </Tablecell>
    </TableRow>
  );
});

export default React.memo(PositionTable);
